import { useMemo } from 'react'
import { Button, Checkbox, Divider, Menu, ScrollArea, Text } from '@mantine/core'
import { byCarNumber } from '@racedash/shared'
import { usePortalTarget } from '../lib/usePortalTarget'
import './carPicker.css'

export interface CarChoice {
  number: string
  klass?: string
  /** Entered by this organisation at this event. */
  ours?: boolean
}

/**
 * Choosing several cars out of a field.
 *
 * A multi-select that renders a pill per selection grows a line every few cars
 * and shoves the rest of the toolbar down the page — which on a sixty-car grid
 * turns a control into a paragraph. So the trigger stays one line whatever is
 * chosen: the number itself when there is one, a count when there are more.
 *
 * The team's own cars sit in their own group at the top. On a full grid the car
 * you came to watch should not have to be hunted for, and putting it first is
 * the difference between reading a list and scanning one.
 *
 * Ordered by car number read as a number, so 7 comes before 58 comes before
 * 436. Never by position: a list that reorders itself every lap is a list you
 * cannot look anything up in.
 */
export default function CarPicker({
  cars,
  value,
  onChange,
  max,
  placeholder = 'Compare with…',
  label = 'Cars',
  width = 190,
}: {
  cars: readonly CarChoice[]
  value: readonly string[]
  onChange: (next: string[]) => void
  /** Beyond this many the chart is unreadable and each one costs a listener. */
  max?: number
  placeholder?: string
  label?: string
  width?: number
}) {
  const portal = usePortalTarget()

  const { ours, rest } = useMemo(() => {
    const sorted = [...cars].sort((a, b) => byCarNumber(a.number, b.number))
    return {
      ours: sorted.filter((c) => c.ours),
      rest: sorted.filter((c) => !c.ours),
    }
  }, [cars])

  const chosen = new Set(value)
  const atLimit = max != null && value.length >= max

  const toggle = (number: string) => {
    if (chosen.has(number)) {
      onChange(value.filter((n) => n !== number))
      return
    }
    if (atLimit) return
    onChange([...value, number])
  }

  // One line, always. The number when a single car is chosen, because that is
  // the thing you would have read off a pill anyway; a count beyond that,
  // because five numbers is not something anybody reads from a button.
  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? `#${value[0]}`
        : `${value.length} cars`

  const row = (c: CarChoice) => {
    const on = chosen.has(c.number)
    return (
      <Menu.Item
        key={c.number}
        // Choosing several cars means several clicks, and a menu that shut
        // after each one would make that four round trips instead of four
        // clicks.
        closeMenuOnClick={false}
        onClick={() => toggle(c.number)}
        disabled={!on && atLimit}
        className="car-pick"
      >
        <Checkbox
          size="xs"
          checked={on}
          readOnly
          tabIndex={-1}
          label={
            <span className="car-pick-label">
              <span className="car-pick-num">{c.number}</span>
              {c.klass && <span className="car-pick-class">{c.klass}</span>}
            </span>
          }
        />
      </Menu.Item>
    )
  }

  return (
    <Menu
      shadow="md"
      width={width + 60}
      position="bottom-start"
      withinPortal
      portalProps={{ target: portal }}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <Button
          size="xs"
          variant="default"
          w={width}
          aria-label={label}
          className="car-pick-trigger"
          data-empty={value.length === 0 || undefined}
        >
          {summary}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <ScrollArea.Autosize mah={320} type="scroll">
          {ours.length > 0 && (
            <>
              <Menu.Label>Ours</Menu.Label>
              {ours.map(row)}
              {rest.length > 0 && <Divider my={4} />}
            </>
          )}
          {rest.length > 0 && (
            <>
              {ours.length > 0 && <Menu.Label>Everyone else</Menu.Label>}
              {rest.map(row)}
            </>
          )}
          {cars.length === 0 && (
            <Text size="xs" c="dimmed" p="xs">
              No other cars yet.
            </Text>
          )}
        </ScrollArea.Autosize>

        {value.length > 0 && (
          <>
            <Divider my={4} />
            <Menu.Item onClick={() => onChange([])} className="car-pick-clear">
              Clear
            </Menu.Item>
          </>
        )}
        {atLimit && (
          <Text size="xs" c="dimmed" px="xs" pb={6}>
            {max} at once is as many as the chart can say anything with.
          </Text>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
